import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Mail, Phone, User, Users, MessageSquare, ArrowLeft, CreditCard, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookPackage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTripDate, setSelectedTripDate] = useState('');
  const [showCustomDateOption, setShowCustomDateOption] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_dates: '',
    group_size: 1,
    message: ''
  });
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  useEffect(() => {
    const fetchPackage = async () => {
      try {
        const response = await axios.get(`${API}/packages/${id}`);
        setPkg(response.data);
        // If package has trip dates, set first one as default
        if (response.data.trip_dates && response.data.trip_dates.length > 0) {
          const firstDate = response.data.trip_dates[0];
          setSelectedTripDate(JSON.stringify(firstDate));
          setFormData(prev => ({
            ...prev,
            preferred_dates: `${firstDate.date} - ${firstDate.end_date}`
          }));
        }
      } catch (error) {
        console.error('Error fetching package:', error);
        toast.error('Package not found');
        navigate('/packages');
      } finally {
        setLoading(false);
      }
    };
    fetchPackage();
  }, [id, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTripDateChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setShowCustomDateOption(true);
      setSelectedTripDate('');
      setFormData(prev => ({ ...prev, preferred_dates: '' }));
    } else {
      setShowCustomDateOption(false);
      setSelectedTripDate(value);
      const tripDate = JSON.parse(value);
      setFormData(prev => ({
        ...prev,
        preferred_dates: `${tripDate.date} - ${tripDate.end_date}`
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreeToTerms) {
      toast.error('Please agree to the Terms & Conditions to proceed.');
      return;
    }
    
    setSubmitting(true);

    try {
      // Parse selected trip date if available
      let bookingData = {
        package_id: id,
        ...formData
      };

      if (selectedTripDate && !showCustomDateOption) {
        const tripDate = JSON.parse(selectedTripDate);
        bookingData.selected_trip_date = tripDate.date;
        bookingData.selected_trip_end_date = tripDate.end_date;
      }

      // Create booking
      const bookingResponse = await axios.post(`${API}/bookings`, bookingData);
      
      const booking = bookingResponse.data;
      
      // Create checkout session
      const checkoutResponse = await axios.post(`${API}/create-checkout-session`, {
        booking_id: booking.id,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe Checkout
      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-maroon text-xl">Loading...</p>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-maroon text-xl">Package not found</p>
      </div>
    );
  }

  const depositAmount = (pkg.price * 0.05).toFixed(2);

  return (
    <div className="py-16" data-testid="book-package-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to={`/packages/${id}`} className="inline-flex items-center space-x-2 text-maroon hover:text-terracotta mb-8">
          <ArrowLeft size={20} />
          <span className="font-semibold">Back to Package Details</span>
        </Link>

        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-maroon mb-4">
            Reserve Your Adventure
          </h1>
          <p className="text-xl text-maroon/70">
            Complete your booking for <span className="font-bold">{pkg.title}</span>
          </p>
        </div>

        {/* Package Summary Card */}
        <div className="card mb-8 bg-sand/30">
          <div className="flex flex-col md:flex-row gap-6">
            <img 
              src={pkg.image_url} 
              alt={pkg.title}
              className="w-full md:w-48 h-32 object-cover rounded-sm"
            />
            <div className="flex-1">
              <h2 className="text-2xl font-heading font-bold text-maroon mb-2">{pkg.title}</h2>
              <p className="text-maroon/70 mb-4">{pkg.destination} • {pkg.duration}</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-maroon/60">Package Price</p>
                  <p className="text-2xl font-bold text-maroon">${pkg.price}</p>
                  <p className="text-xs text-maroon/60">per person</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-maroon/60">Deposit Today (5%)</p>
                  <p className="text-2xl font-bold text-terracotta">${depositAmount}</p>
                  <Link to="/terms#refund-policy" className="text-xs text-maroon/60 hover:text-terracotta underline">
                    non-refundable
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Form */}
        <div className="card">
          <h3 className="text-xl font-heading font-bold text-maroon mb-6 flex items-center gap-2">
            <CreditCard size={24} />
            Traveler Information
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center space-x-2">
                <User size={16} />
                <span>Full Name *</span>
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="input-field"
                placeholder="Enter your full name"
                data-testid="booking-name-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center space-x-2">
                  <Mail size={16} />
                  <span>Email *</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="input-field"
                  placeholder="your@email.com"
                  data-testid="booking-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center space-x-2">
                  <Phone size={16} />
                  <span>Phone *</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="input-field"
                  placeholder="+1 (555) 123-4567"
                  data-testid="booking-phone-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="trip_date" className="flex items-center space-x-2">
                  <Calendar size={16} />
                  <span>Select Travel Date *</span>
                </Label>
                {pkg.trip_dates && pkg.trip_dates.length > 0 ? (
                  <>
                    <select
                      id="trip_date"
                      value={showCustomDateOption ? 'custom' : selectedTripDate}
                      onChange={handleTripDateChange}
                      className="w-full px-4 py-3 bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm text-maroon"
                      data-testid="booking-trip-date-select"
                    >
                      {pkg.trip_dates.map((tripDate, idx) => (
                        <option key={idx} value={JSON.stringify(tripDate)}>
                          {tripDate.date} to {tripDate.end_date} ({tripDate.available_spots || 'Limited'} spots)
                        </option>
                      ))}
                      <option value="custom">Different dates? Request custom dates</option>
                    </select>
                    
                    {showCustomDateOption && (
                      <div className="mt-4 p-4 bg-terracotta/10 rounded-sm border-2 border-terracotta/30">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="text-terracotta flex-shrink-0 mt-0.5" size={20} />
                          <div>
                            <p className="font-semibold text-maroon mb-2">Need Different Dates?</p>
                            <p className="text-sm text-maroon/80 mb-3">
                              For custom travel dates outside our scheduled departures, please contact our team:
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                              <Link 
                                to="/consultation" 
                                className="btn-primary text-center text-sm py-2"
                              >
                                Schedule Consultation
                              </Link>
                              <a 
                                href="tel:+18335688090" 
                                className="btn-secondary text-center text-sm py-2"
                              >
                                Call (833) 568-8090
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <Input
                    id="preferred_dates"
                    name="preferred_dates"
                    placeholder="e.g., June 15-22, 2026"
                    value={formData.preferred_dates}
                    onChange={handleChange}
                    required
                    className="input-field"
                    data-testid="booking-dates-input"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="group_size" className="flex items-center space-x-2">
                  <Users size={16} />
                  <span>Number of Travelers *</span>
                </Label>
                <Input
                  id="group_size"
                  name="group_size"
                  type="number"
                  min="1"
                  value={formData.group_size}
                  onChange={handleChange}
                  required
                  className="input-field"
                  data-testid="booking-groupsize-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="flex items-center space-x-2">
                <MessageSquare size={16} />
                <span>Special Requests or Notes</span>
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
                placeholder="Any dietary requirements, accessibility needs, or special requests..."
                data-testid="booking-message-input"
              />
            </div>

            <div className="bg-sand/50 p-4 rounded-sm border border-maroon/10">
              <p className="text-sm text-maroon/80">
                <strong>Important:</strong> The 5% deposit of <strong>${depositAmount}</strong> is non-refundable. 
                Our travel specialists will contact you within 24-48 hours to finalize your travel plans. 
                The remaining balance is due 60 days before departure.
              </p>
            </div>

            <div className="flex items-start space-x-3 p-4 bg-white border-2 border-maroon/20 rounded-sm">
              <Checkbox
                id="terms"
                checked={agreeToTerms}
                onCheckedChange={setAgreeToTerms}
                className="mt-0.5"
                data-testid="terms-checkbox"
              />
              <Label htmlFor="terms" className="text-sm text-maroon/80 cursor-pointer leading-relaxed">
                I agree to the{' '}
                <Link to="/terms" target="_blank" className="text-maroon font-semibold hover:text-terracotta underline">
                  Terms & Conditions
                </Link>{' '}
                and understand that the 5% deposit is <strong>non-refundable</strong>. I have read the{' '}
                <Link to="/terms#refund-policy" target="_blank" className="text-maroon font-semibold hover:text-terracotta underline">
                  Refund Policy
                </Link>.
              </Label>
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full text-lg py-6"
              disabled={submitting || !agreeToTerms}
              data-testid="proceed-to-payment-btn"
            >
              {submitting ? 'Processing...' : `Proceed to Payment - $${depositAmount}`}
            </Button>
          </form>
        </div>

        <div className="mt-8 text-center">
          <p className="text-maroon/70">
            Questions? Call us at <a href="tel:+18335688090" className="font-bold text-maroon hover:text-terracotta underline">(833) 568-8090</a> or email{' '}
            <a href="mailto:hello@chimbatravel.com" className="font-bold text-maroon hover:text-terracotta underline">hello@chimbatravel.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BookPackage;
